import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { XMarkIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

// Stable constant — avoids recreating an inline function on every render
const NODE_CANVAS_MODE = () => 'replace';

const DEFAULT_COLOR = '#7CC4FC';     // primary-300
const SELECTED_COLOR = '#4f46e5';    // indigo-600
const NEIGHBOR_COLOR = '#3AABFB';    // primary-400
const DIMMED_COLOR = '#d1d5db';      // gray-300

// ── Connected-component BFS ──────────────────────────────────────────────────
function computeComponents(nodes, links) {
  const adj = new Map(nodes.map(n => [n.id, new Set()]));
  links.forEach(l => {
    const s = typeof l.source === 'object' ? l.source.id : l.source;
    const t = typeof l.target === 'object' ? l.target.id : l.target;
    adj.get(s)?.add(t);
    adj.get(t)?.add(s);
  });

  const visited = new Set();
  const components = [];
  nodes.forEach(node => {
    if (visited.has(node.id)) return;
    const members = [];
    const queue = [node.id];
    while (queue.length) {
      const id = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      members.push(id);
      adj.get(id)?.forEach(nb => { if (!visited.has(nb)) queue.push(nb); });
    }
    components.push(members);
  });
  return components;
}

const TagGraph = ({ tags, bookmarks, onNavigate, isDark = false }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  // Set to true when the D3 simulation has fully settled — guards centerAt calls
  const engineSettledRef = useRef(false);

  // Track container width for responsive canvas
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // ── Co-occurrence graph data ───────────────────────────────────────────────
  const graphData = useMemo(() => {
    if (!bookmarks || !tags) return { nodes: [], links: [] };

    const linkMap = new Map();
    bookmarks.forEach(b => {
      const t = b.tags || [];
      for (let i = 0; i < t.length; i++) {
        for (let j = i + 1; j < t.length; j++) {
          const key = [t[i], t[j]].sort().join('||');
          linkMap.set(key, (linkMap.get(key) || 0) + 1);
        }
      }
    });

    const nodes = tags.map(t => ({ id: t.tagName, count: t.count }));

    // Only keep links where BOTH endpoints exist in the nodes (tags table).
    // Bookmark tags can reference names not yet in the tags table (race condition,
    // sync delay, or stale data). A dangling link causes D3 to create a phantom
    // node with NaN x/y — those nodes are invisible and corrupt zoomToFit's
    // bounding-box calculation, making all nodes disappear.
    const nodeIds = new Set(nodes.map(n => n.id));
    const links = [];
    linkMap.forEach((value, key) => {
      const [source, target] = key.split('||');
      if (nodeIds.has(source) && nodeIds.has(target)) {
        links.push({ source, target, value });
      }
    });

    return { nodes, links };
  }, [bookmarks, tags]);

  // ── Split connected nodes from isolated ones ───────────────────────────────
  const { connectedGraphData, isolatedNodes, clusters } = useMemo(() => {
    const components = computeComponents(graphData.nodes, graphData.links);
    const isolatedIds = new Set(
      components.filter(c => c.length === 1).map(c => c[0])
    );

    const connectedNodes = graphData.nodes.filter(n => !isolatedIds.has(n.id));
    const isolated = graphData.nodes.filter(n => isolatedIds.has(n.id))
      .sort((a, b) => b.count - a.count);

    // Clusters: multi-node components, sorted largest first, nodes sorted by count desc
    const clusterList = components
      .filter(c => c.length > 1)
      .map(ids => {
        const members = ids
          .map(id => graphData.nodes.find(n => n.id === id))
          .filter(Boolean)
          .sort((a, b) => b.count - a.count);
        return { label: members[0].id, members, ids: new Set(ids) };
      })
      .sort((a, b) => b.members.length - a.members.length);

    return {
      connectedGraphData: { nodes: connectedNodes, links: graphData.links },
      isolatedNodes: isolated,
      clusters: clusterList,
    };
  }, [graphData]);

  // ── Selection state ────────────────────────────────────────────────────────
  const { neighborSet, neighborLinks } = useMemo(() => {
    if (!selectedNode) return { neighborSet: new Set(), neighborLinks: new Set() };
    const nSet = new Set();
    const lSet = new Set();
    graphData.links.forEach(link => {
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
      if (srcId === selectedNode.id) { nSet.add(tgtId); lSet.add(link); }
      if (tgtId === selectedNode.id) { nSet.add(srcId); lSet.add(link); }
    });
    return { neighborSet: nSet, neighborLinks: lSet };
  }, [selectedNode, graphData.links]);

  const connectedTags = useMemo(() => {
    if (!selectedNode) return [];
    const result = [];
    graphData.links.forEach(link => {
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
      if (srcId === selectedNode.id) result.push({ tagName: tgtId, shared: link.value });
      else if (tgtId === selectedNode.id) result.push({ tagName: srcId, shared: link.value });
    });
    return result.sort((a, b) => b.shared - a.shared).slice(0, 8);
  }, [selectedNode, graphData.links]);

  // ── Stable node/link accessors (avoid prop churn on every render) ──────────
  const getNodeVal = useCallback(node => Math.max(3, node.count * 2), []);
  const getLinkWidth = useCallback(link => Math.max(1, link.value), []);

  // ── Canvas rendering ───────────────────────────────────────────────────────
  const getNodeColor = useCallback((node) => {
    if (!selectedNode) return DEFAULT_COLOR;
    if (node.id === selectedNode.id) return SELECTED_COLOR;
    if (neighborSet.has(node.id)) return NEIGHBOR_COLOR;
    return DIMMED_COLOR;
  }, [selectedNode, neighborSet]);

  const getLinkColor = useCallback((link) => {
    if (!selectedNode) return '#B9DFFD';  // primary-200
    if (neighborLinks.has(link)) return '#818cf8';
    return '#e5e7eb';
  }, [selectedNode, neighborLinks]);

  const drawNodeLabel = useCallback((node, ctx, globalScale) => {
    const color = getNodeColor(node);
    const radius = Math.max(3, node.count * 2);
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    if (globalScale > 1.5 || (selectedNode && node.id === selectedNode.id)) {
      const fontSize = Math.max(8, 10 / globalScale);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = isDark ? '#e2e8f0' : '#1e293b';
      ctx.fillText(node.id, node.x, node.y + radius + 2);
    }
  }, [getNodeColor, selectedNode, isDark]);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(prev => (prev && prev.id === node.id ? null : node));
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // ── Graph controls ─────────────────────────────────────────────────────────

  // Called once when the D3 simulation finishes settling.
  // Resets camera to a known-good state then fits all nodes into view.
  const handleEngineStop = useCallback(() => {
    engineSettledRef.current = true;
    graphRef.current?.zoomToFit(400, 40);
  }, []);

  // Resets the d3-zoom transform to a safe identity state before fitting.
  // This recovers from any NaN/corrupt camera state caused by rapid zoom or
  // bad centerAt calls before the simulation settled.
  const handleFitView = useCallback(() => {
    if (!graphRef.current) return;
    graphRef.current.zoom(1, 0);
    graphRef.current.centerAt(0, 0, 0);
    // Brief delay lets the synchronous transform reset propagate before zoomToFit reads bounds
    setTimeout(() => graphRef.current?.zoomToFit(400, 40), 50);
  }, []);

  const handleFocusCluster = useCallback((clusterIds) => {
    if (!graphRef.current) return;
    graphRef.current.zoomToFit(400, 60, node => clusterIds.has(node.id));
  }, []);

  const handleFocusNode = useCallback((nodeId) => {
    const node = connectedGraphData.nodes.find(n => n.id === nodeId);
    if (!node || !graphRef.current) return;
    setSelectedNode(node);
    // Only animate camera if the simulation has settled and node has valid coordinates.
    // Calling centerAt with undefined/NaN positions corrupts the d3-zoom transform.
    if (engineSettledRef.current && node.x != null && !isNaN(node.x)) {
      graphRef.current.centerAt(node.x, node.y, 400);
      graphRef.current.zoom(3, 400);
    }
  }, [connectedGraphData.nodes]);

  // ── Empty states ───────────────────────────────────────────────────────────
  const hasConnectedNodes = connectedGraphData.nodes.length > 0;

  if (!hasConnectedNodes && isolatedNodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
        No tags to display.
      </div>
    );
  }

  if (!hasConnectedNodes) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
        <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">No tag connections yet.</p>
        <p className="text-sm">Bookmarks need at least two tags each to show relationships.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 md:items-start">

        {/* ── Tag index panel ──────────────────────────────────────────────── */}
        <div className="w-full md:w-44 md:flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-y-auto max-h-40 md:max-h-[556px]">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tag Index</p>
          </div>

          <div className="p-2 space-y-3">
            {clusters.map((cluster, i) => (
              <div key={i}>
                <button
                  onClick={() => handleFocusCluster(cluster.ids)}
                  title="Zoom to this cluster"
                  className="w-full text-left text-xs font-semibold text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 px-1 py-0.5 rounded transition-colors truncate"
                >
                  {cluster.label}
                  <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">({cluster.members.length})</span>
                </button>
                <div className="ml-2 mt-0.5 space-y-0.5">
                  {cluster.members.map(node => (
                    <button
                      key={node.id}
                      onClick={() => handleFocusNode(node.id)}
                      className={`w-full text-left text-xs px-1.5 py-0.5 rounded transition-colors truncate ${
                        selectedNode?.id === node.id
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      {node.id}
                      <span className="text-gray-400 dark:text-gray-500 ml-1">{node.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {isolatedNodes.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 py-0.5">
                  No connections
                </p>
                <div className="ml-2 mt-0.5 space-y-0.5">
                  {isolatedNodes.map(node => (
                    <button
                      key={node.id}
                      onClick={() => onNavigate(node.id)}
                      className="w-full text-left text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors truncate"
                    >
                      {node.id}
                      <span className="ml-1">{node.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Graph + controls ─────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-3">

          {/* Fit to view button */}
          <div className="flex justify-end">
            <button
              onClick={handleFitView}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm transition-colors"
              title="Fit all nodes into view"
            >
              <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
              Fit to view
            </button>
          </div>

          {/* Canvas */}
          <div ref={containerRef} className="bg-white dark:bg-gray-900 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
            <ForceGraph2D
              ref={graphRef}
              graphData={connectedGraphData}
              nodeVal={getNodeVal}
              nodeColor={getNodeColor}
              linkWidth={getLinkWidth}
              linkColor={getLinkColor}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
              nodeCanvasObject={drawNodeLabel}
              nodeCanvasObjectMode={NODE_CANVAS_MODE}
              onEngineStop={handleEngineStop}
              enableNodeDrag={false}
              warmupTicks={100}
              cooldownTime={3000}
              d3AlphaMin={0.001}
              minZoom={0.1}
              maxZoom={8}
              width={containerWidth}
              height={480}
              d3VelocityDecay={0.3}
              backgroundColor={isDark ? '#111827' : '#ffffff'}
            />
          </div>

          {/* Selection info panel */}
          {selectedNode && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-indigo-200 dark:border-indigo-700 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 px-3 py-1 rounded-full text-sm font-medium">
                      {selectedNode.id}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedNode.count} bookmark{selectedNode.count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {connectedTags.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Connected tags:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {connectedTags.map(ct => (
                          <button
                            key={ct.tagName}
                            onClick={() => handleFocusNode(ct.tagName)}
                            className="text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 px-2 py-0.5 rounded-full transition-colors"
                          >
                            {ct.tagName} <span className="text-primary-400 dark:text-primary-500">({ct.shared} shared)</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => onNavigate(selectedNode.id)}
                    className="mt-3 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium transition-colors"
                  >
                    View bookmarks →
                  </button>
                </div>

                <button
                  onClick={() => setSelectedNode(null)}
                  className="ml-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Isolated nodes (no co-occurrences) ─────────────────────────────── */}
      {isolatedNodes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
            Tags with no connections
          </p>
          <div className="flex flex-wrap gap-2">
            {isolatedNodes.map(node => (
              <button
                key={node.id}
                onClick={() => onNavigate(node.id)}
                title={`${node.count} bookmark${node.count !== 1 ? 's' : ''}`}
                className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400 px-3 py-1 rounded-full text-sm transition-colors"
              >
                {node.id}
                <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">{node.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagGraph;
